import {useMemo, useRef} from 'react';
import {assert} from 'assert-ts';
import isEqual from 'lodash/isEqual';
import {useAppSelector} from 'store/hooks';
import type {
  ExpressionImportProps,
  ManifestationImportProps,
  WorkImportProps,
} from '../types';
import {
  selectMetadataEditExportsExpressionById,
  selectMetadataEditExportsManifestationById,
  selectMetadataEditExportsWork,
} from '../metadataEditSlice';
import {useCreateSelectorById} from './useCreateSelectorById';

/**
 * Returns either WorkImportProps, ExpressionImportProps, or ManifestationImportProps
 * But does not crash if manifestation or expression is not found.
 */
export const useChangeRequestImportProps = (
  manifestationId?: string,
  expressionId?: string,
): ManifestationImportProps | ExpressionImportProps | WorkImportProps => {
  // Work props
  const workExports = useAppSelector(selectMetadataEditExportsWork);

  // Expression props
  const expressionExportsSelector = useCreateSelectorById(
    expressionId || 'DummyExpressionId',
    selectMetadataEditExportsExpressionById,
  );
  const expressionExports = useAppSelector(expressionExportsSelector);

  // Manifestation props
  const manifestationExportsSelector = useCreateSelectorById(
    manifestationId || 'DummyManifestationId',
    selectMetadataEditExportsManifestationById,
  );
  const manifestationExports = useAppSelector(manifestationExportsSelector);

  const exportsRef = useRef<
    ManifestationImportProps | ExpressionImportProps | WorkImportProps
  >(undefined);

  return useMemo(() => {
    const newExports = {
      work: workExports,
      expression: expressionExports,
      manifestation: manifestationExports,
    };

    if (!isEqual(newExports, exportsRef.current)) {
      exportsRef.current = newExports;
    }

    return assert(exportsRef.current);
  }, [expressionExports, manifestationExports, workExports]);
};
